import { useState } from 'react';

import { faArrowsCross } from '@awesome.me/kit-f3dc60f0d0/icons/classic/solid';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@repo/videa-components';
import { useAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { type FmxIndexesByType } from '@/@types/latestImagesV2';
import { useUpdateFmxSort } from '@/react-query/useUpdateFmxSort';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { FmxSortKey, selectedFmxSortKeyAtom } from '../../../atoms';

import { FmxSortOption } from './FmxSortOption';

interface Props {
  estimatedVisitId?: number | null;
  fmxIndexesByType: FmxIndexesByType[];
}

export const FmxSortButton = ({
  estimatedVisitId,
  fmxIndexesByType,
}: Props) => {
  const { mutate: updateFmxSort, isPending, error } = useUpdateFmxSort();
  const { trackEvent } = useTrackEvent();
  const { id: imagingPatientId } = useParams<{ id: string }>();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedFmxSortKey, setSelectedFmxSortKey] = useAtom(
    selectedFmxSortKeyAtom,
  );
  const selectedFmxSortDescription = fmxIndexesByType.find(
    (item) => item.key === selectedFmxSortKey,
  )?.description;

  const handleSave = () => {
    if (!estimatedVisitId || !imagingPatientId || !selectedFmxSortKey) {
      return;
    }

    trackEvent(mixpanelEvents.fmxSortSaveClick, {
      selectedFmxSortKey,
      selectedFmxSortDescription,
    });
    if (selectedFmxSortKey === FmxSortKey.CURRENT_ORDER) {
      // This option is essentially selected as soon as any sort option is saved
      // since at that point users have the power to manually sort images. We
      // don't need to promote this to the backend.
      setIsSheetOpen(false);
      return;
    }
    updateFmxSort(
      {
        imagingPatientId,
        estimatedVisitId,
        fmxSortKeyToApply: selectedFmxSortKey,
      },
      {
        onSuccess: () => {
          setIsSheetOpen(false);
          setSelectedFmxSortKey(FmxSortKey.CURRENT_ORDER);
        },
      },
    );
  };

  return (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetTrigger
        className="vai-bg-zinc-800 hover:vai-bg-zinc-950 vai-text-zinc-400 vai-rounded-sm vai-px-4 vai-py-2 vai-flex vai-items-center vai-gap-2"
        onClick={() => {
          trackEvent(mixpanelEvents.fmxSortButtonClick, {
            selectedFmxSortKey,
            selectedFmxSortDescription,
          });
        }}
      >
        <FontAwesomeIcon icon={faArrowsCross} />
        <span className="vai-font-inter vai-text-sm vai-font-medium">
          <Trans>Sort</Trans>
        </span>
      </SheetTrigger>
      <SheetContent
        className="vai-p-6 vai-bg-zinc-950 vai-border-zinc-800 vai-flex vai-flex-col"
        closeButtonClassName="vai-text-zinc-50"
        hideOverlay
      >
        <SheetHeader>
          <SheetTitle className="vai-text-zinc-50 vai-font-inter">
            <Trans>FMX Sorting Options</Trans>
          </SheetTitle>
          <SheetDescription className="vai-text-zinc-400 vai-font-inter">
            <Trans>
              Choose how you want your FMX images to be organized and displayed.
            </Trans>
          </SheetDescription>
        </SheetHeader>

        <div className="vai-flex-1 vai-flex vai-flex-col vai-gap-6 vai-overflow-y-auto">
          {fmxIndexesByType.map((item) => (
            <FmxSortOption
              fmxSortDescription={item.description}
              fmxSortKey={item.key}
              isSelected={item.key === selectedFmxSortKey}
              key={item.key}
              fmxIndexes={item.fmxIndexes}
              onClick={() => {
                trackEvent(mixpanelEvents.fmxSortOptionClick, {
                  selectedFmxSortKey: item.key,
                  selectedFmxSortDescription: item.description,
                });
                setSelectedFmxSortKey(item.key);
              }}
            />
          ))}
          <FmxSortOption
            fmxSortKey={FmxSortKey.CURRENT_ORDER}
            isSelected={selectedFmxSortKey === FmxSortKey.CURRENT_ORDER}
            onClick={() => {
              trackEvent(mixpanelEvents.fmxSortOptionClick, {
                selectedFmxSortKey: FmxSortKey.CURRENT_ORDER,
              });
              setSelectedFmxSortKey(FmxSortKey.CURRENT_ORDER);
            }}
          />
          {!fmxIndexesByType.length && (
            <p className="vai-font-inter vai-text-zinc-400">
              <Trans>No FMX sorting options found</Trans>
            </p>
          )}
        </div>

        <SheetFooter className="vai-flex !vai-flex-col vai-gap-4">
          {!!error && (
            <Alert variant="destructive">
              <AlertTitle>
                <Trans>Error</Trans>
              </AlertTitle>
              <AlertDescription>{error.message}</AlertDescription>
            </Alert>
          )}
          <div className="vai-flex vai-justify-end vai-gap-1">
            <SheetClose asChild>
              <Button
                onClick={() => {
                  trackEvent(mixpanelEvents.fmxSortCancelClick, {
                    selectedFmxSortKey,
                    selectedFmxSortDescription,
                  });
                  setSelectedFmxSortKey(FmxSortKey.CURRENT_ORDER);
                }}
                variant="outline"
                className="vai-bg-transparent vai-text-zinc-50 hover:vai-text-zinc-50 hover:vai-bg-zinc-950 vai-border-zinc-800 "
              >
                <Trans>Cancel</Trans>
              </Button>
            </SheetClose>
            <Button
              loading={isPending}
              disabled={!selectedFmxSortKey}
              onClick={handleSave}
              type="submit"
            >
              <Trans>Save for this visit</Trans>
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
